import type { z } from 'zod';

export type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

/**
 * One API endpoint: method, path (relative to the API's `/v1` base, `:name` marks a path
 * parameter) and the schemas of its body, query and success response. `response: null` means the
 * endpoint answers 204 with no body.
 */
export interface Contract<
  Path extends string = string,
  Body extends z.ZodType | undefined = z.ZodType | undefined,
  Query extends z.ZodType | undefined = z.ZodType | undefined,
  Response extends z.ZodType | null = z.ZodType | null,
> {
  readonly method: HttpMethod;
  readonly path: Path;
  readonly body?: Body;
  readonly query?: Query;
  readonly response: Response;
}

export function defineContract<
  const Path extends string,
  Body extends z.ZodType | undefined = undefined,
  Query extends z.ZodType | undefined = undefined,
  Response extends z.ZodType | null = null,
>(contract: Contract<Path, Body, Query, Response>): Contract<Path, Body, Query, Response> {
  return contract;
}

/** `'/a/:id/b/:roleId'` -> `'id' | 'roleId'`. */
export type PathParams<Path extends string> = Path extends `${string}:${infer Rest}`
  ? Rest extends `${infer Name}/${infer Tail}`
    ? Name | PathParams<`/${Tail}`>
    : Rest
  : never;
